/* eslint-disable import/first */
// @flow
import React, { type Node } from 'react';
import { kebabCase, find } from 'lodash';
import { LegacyTrack, urlCatalog } from 'angieslist-app-platform';
import { Badge, MenuItem, Nav, NavDropdown, NavItem } from 'angieslist-react-components';
import Experiment from 'angieslist-app-platform/lib/common/experiment/v3/Experiment';
import type { ServiceProvider, Entitlement } from '../types';
import './businessNav.scss';

const isServer = typeof window === 'undefined';

const navItem = ({
  text,
  icon,
  activityLocation,
  serviceProvider,
  unreadCount,
}: {
  text: string,
  icon: string,
  activityLocation: string,
  serviceProvider: ServiceProvider,
  unreadCount?: number,
}) => (
  <LegacyTrack>
    <div
      id={`business-nav--${kebabCase(text)}-${serviceProvider.id}`}
      className="business-nav-item"
      data-track-service-provider-id={serviceProvider.id}
      data-track-activity-location={activityLocation}
    >
      <If condition={unreadCount && unreadCount > 0}>
        <Badge
          bsStyle="danger"
          style={{
          position: 'absolute',
          padding: 3,
          top: 5,
          zIndex: 10,
        }}
        >
          {unreadCount}
        </Badge>
      </If>
      <i className={`business-nav-item-icon icon icon-2x ${icon}`} />
      <span className="business-nav-title">{text}</span>
    </div>
  </LegacyTrack>
);

const menuItem = ({
  text,
  activityLocation,
  serviceProvider,
}: {
    text: string,
    activityLocation: string,
    serviceProvider: ServiceProvider,
  }) => (
    <LegacyTrack>
      <p
        id={`business-nav--${kebabCase(text)}-${serviceProvider.id}`}
        data-track-service-provider-id={serviceProvider.id}
        data-track-activity-location={activityLocation}
      >
        {text}
      </p>
    </LegacyTrack>
);

type NavItemProps = {|
  +href: string,
  +eventKey: string,
|};

type NavItemPropsObject = { [key: string]: NavItemProps };

const getNavItemProps = (id, profileUrl) => ({
  home: {
    href: `${urlCatalog.office.home()}/${id}`,
    eventKey: 'home',
  },
  leads: {
    href: urlCatalog.office.leadBoard(id),
    eventKey: 'leads',
  },
  reviews: {
    href: urlCatalog.office.reviews(id),
    eventKey: 'reviews',
  },
  requestReviews: {
    href: urlCatalog.office.requestReviews(id),
    eventKey: 'requestReviews',
  },
  profile: {
    href: urlCatalog.office.spProfile.about(id),
    eventKey: 'profile',
  },
  viewProfile: {
    href: profileUrl || urlCatalog.member.serviceProvider(id),
    eventKey: 'viewMyBusiness',
  },
  myBusiness: {
    href: urlCatalog.office.spProfile.edit(id),
    eventKey: 'myBusiness',
  },
  showcaseIndex: {
    href: urlCatalog.office.spProfile.showcases(id),
    eventKey: 'showcaseIndex',
  },
  photoGallery: {
    href: urlCatalog.office.spProfile.images(id),
    eventKey: 'photoGallery',
  },
  businessHighlights: {
    href: urlCatalog.office.spProfile.businessHighlights(id),
    eventKey: 'businessHighlights',
  },
  categories: {
    href: urlCatalog.office.manageCategories(id),
    eventKey: 'categories',
  },
  serviceAreas: {
    href: urlCatalog.office.spProfile.serviceAreas(id),
    eventKey: 'serviceAreas',
  },
  associates: {
    href: urlCatalog.office.associates(id),
    eventKey: 'associates',
  },
  payment: {
    href: urlCatalog.office.paymentMethod(id),
    eventKey: 'payment',
  },
  priceList: {
    href: urlCatalog.office.bmt.priceList(id),
    eventKey: 'priceList',
  },
  customers: {
    href: urlCatalog.office.bmt.customers(id),
    eventKey: 'customers',
  },
}: NavItemPropsObject);

function getActiveKey(navItemProps: NavItemPropsObject): ?string {
  if (isServer) {
    return '';
  }
  const values = ((Object.values(navItemProps): any): NavItemProps[]);
  const activeItem = values.find(x => window.location.href.includes(x.href));
  return activeItem && activeItem.eventKey;
}

type Props = {
  activityLocation: string,
  serviceProvider: ServiceProvider,
  enableBmtQuickBooksSync: boolean,
  entitlements: Entitlement[],
  accountingSyncUrl: string,
};

const BusinessNavComponent = ({
  activityLocation,
  serviceProvider,
  enableBmtQuickBooksSync,
  entitlements,
  accountingSyncUrl,
}: Props): Node => {
  const hasBmt = find(entitlements, e => e.permission === 'BusinessManagementTools' && e.scopeId === String(serviceProvider.id)) || false;
  const isAdmin = find(entitlements, e => e.permission === 'admin' && e.scopeId === String(serviceProvider.id) && e.scope === 'ServiceProvider');
  const showQbo = enableBmtQuickBooksSync && isAdmin && hasBmt;
  const navItemProps = getNavItemProps(serviceProvider.id, serviceProvider.profileUrl);

  return (
    <Nav
      bsStyle="pills"
      className="business-nav"
      activeKey={getActiveKey(navItemProps)}
    >
      <Experiment name="combinedLeadsAndMessages" />
      <NavItem
        {...navItemProps.home}
        data-intercom-target="Home"
      >
        {navItem({
          text: 'Home',
          icon: 'icon-home_v2',
          activityLocation,
          serviceProvider,
        })}
      </NavItem>
      <NavItem
        {...navItemProps.leads}
        data-intercom-target="Leads"
      >
        {navItem({
          text: 'Leads',
          icon: 'icon-leads_v2',
          activityLocation,
          serviceProvider,
          unreadCount: serviceProvider.unreadLeadCount,
        })}
      </NavItem>
      <NavDropdown
        id={`business-nav--reviews-dropdown-${serviceProvider.id}`}
        title={navItem({
                text: 'Reviews',
                icon: 'icon-star',
                activityLocation,
                serviceProvider,
                unreadCount: serviceProvider.unreadReviewCount,
              })}
        className="business-nav-item-right"
        data-intercom-target="Reviews"
      >
        <MenuItem
          {...navItemProps.reviews}
        >
          {menuItem({
                text: 'View Reviews',
                activityLocation,
                serviceProvider,
              })}
        </MenuItem>
        <MenuItem
          {...navItemProps.requestReviews}
          data-intercom-target="Request Reviews"
        >
          {menuItem({
              text: 'Request Reviews',
              activityLocation,
              serviceProvider,
            })}
        </MenuItem>
      </NavDropdown>
      <NavDropdown
        id={`business-nav--mybusiness-dropdown-${serviceProvider.id}`}
        title={navItem({
                text: 'My Business',
                icon: 'icon-business_v2',
                activityLocation,
                serviceProvider,
              })}
        className="business-nav-item-right"
        data-intercom-target="My Business"
      >
        <MenuItem
          {...navItemProps.profile}
          className="submenu-item"
          data-intercom-target="Update My Business"
        >
          {menuItem({
            text: 'Business profile',
            activityLocation,
            serviceProvider,
          })}
        </MenuItem>
        <MenuItem
          className="submenu-item"
          {...navItemProps.categories}
        >
          {menuItem({
            text: 'Categories',
            activityLocation,
            serviceProvider,
          })}
        </MenuItem>
        <MenuItem
          className="submenu-item"
          {...navItemProps.serviceAreas}
        >
          {menuItem({
            text: 'Service Areas',
            activityLocation,
            serviceProvider,
          })}
        </MenuItem>
        <MenuItem
          className="submenu-item"
          {...navItemProps.associates}
        >
          {menuItem({
            text: 'Associates',
            activityLocation,
            serviceProvider,
          })}
        </MenuItem>
        <If condition={serviceProvider.isAdvertiser}>
          <MenuItem
            className="submenu-item"
            {...navItemProps.payment}
          >
            {menuItem({
              text: 'Manage Agreements',
              activityLocation,
              serviceProvider,
            })}
          </MenuItem>
        </If>
        <If condition={hasBmt}>
          <MenuItem divider />
          <MenuItem
            {...navItemProps.myBusiness}
            data-intercom-target="Manage My Business"
          >
            {menuItem({
            text: 'Manage My Business',
            activityLocation,
            serviceProvider,
            })}
          </MenuItem>
          <MenuItem
            className="submenu-item"
            {...navItemProps.customers}
            data-intercom-target="Customer List"
          >
            {menuItem({
            text: 'Customer List',
            activityLocation,
            serviceProvider,
          })}
          </MenuItem>

          <MenuItem
            className="submenu-item"
            {...navItemProps.priceList}
            data-intercom-target="Price List"
          >
            {menuItem({
          text: 'Price List',
          activityLocation,
          serviceProvider,
          })}
          </MenuItem>
          <If condition={showQbo}>
            <MenuItem
              className="submenu-item"
              href={`${accountingSyncUrl}&serviceProviderId=${serviceProvider.id}`}
              eventKey="qboSync"
              data-intercom-target="QuickBooks Online Sync"
              target="_blank"
            >
              {menuItem({
            text: 'QuickBooks Online Sync',
            activityLocation,
            serviceProvider,
          })}
            </MenuItem>
          </If>
        </If>
      </NavDropdown>
    </Nav>
  );
};

export default BusinessNavComponent;



// WEBPACK FOOTER //
// ./src/app/browser/BusinessNav.js